import { useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Link,
  Spinner,
  Text,
  Title,
} from '@nimbus-ds/components';
import { OrderDetailsResponseDto } from '@tiendanube/common';
import ResolvedInvoiceStatusDto from '@tiendanube/common/src/domains/orders/commons/ResolvedInvoiceStatusDto';
import {
  ResolvedInvoiceStatus,
  InvoiceOperation as InvoiceOperationEnum,
  InvoiceOperation,
} from '@tiendanube/common/src/enums';
import { useModal } from 'commons/hooks';
import { useAdminLinks } from 'domains/Auth/hooks';
import { configurationsRoutes } from 'domains/Configurations';
import useOrderInvoices from 'domains/Orders/Orders/hooks/useOrderInvoices';
import { UpdateInvoiceEventType } from 'domains/Orders/Orders/ordersService';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import InvoiceErrors from './InvoiceErrors';
import InvoiceOperationModal from './InvoiceModal/InvoiceOperationModal';
import Invoices from './Invoices';

interface CardInvoiceProps {
  orderId: string;
  fulfillmentOrderId: string;
  hasCompleteCustomerInfo: boolean;
  isValidInvoiceStatus: boolean;
  isExternalInvoicesEnabled: boolean;
  orderDetails: OrderDetailsResponseDto;
  onInvoiceRequestSuccess: () => void;
  onInvoiceError: () => void;
  onInvoiceOperationSuccess: (operationType: UpdateInvoiceEventType) => void;
  onInvoiceOperationError: (operationType: UpdateInvoiceEventType) => void;
  onLoading: () => void;
}

function CardInvoice({
  orderId,
  fulfillmentOrderId,
  hasCompleteCustomerInfo,
  isValidInvoiceStatus,
  isExternalInvoicesEnabled,
  orderDetails,
  onInvoiceRequestSuccess,
  onInvoiceError,
  onInvoiceOperationSuccess,
  onInvoiceOperationError,
  onLoading,
}: CardInvoiceProps) {
  const t = useTranslationOrders();
  const { getNewAdminLink } = useAdminLinks();

  const isInvoiceIssueAvailable =
    isValidInvoiceStatus && hasCompleteCustomerInfo;

  const [invoiceStatus, setInvoiceStatus] =
    useState<ResolvedInvoiceStatusDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOperationLoading, setIsOperationLoading] = useState<boolean>(false);
  const [operationReason, setOperationReason] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [invoiceOperation, setInvoiceOperation] =
    useState<InvoiceOperationEnum | null>(null);

  const {
    handleInvoiceSubmission,
    fetchResolvedInvoices,
    handleConfirmInvoiceOperation,
  } = useOrderInvoices({
    orderId,
    fulfillmentOrderId,
    operationReason,
    onInvoiceRequestSuccess: async () => {
      await fetchResolvedInvoices(setInvoiceStatus);
      onInvoiceRequestSuccess();
    },
    onInvoiceError,
    onInvoiceOperationSuccess,
    onInvoiceOperationError,
    onLoading,
  });

  const [
    isInvoiceOperationModalOpen,
    openInvoiceOperationModal,
    handleHideInvoiceOperationModal,
  ] = useModal();

  const handleOpenInvoiceOperationModal = (
    operation: InvoiceOperationEnum,
    number: string,
    invoiceCorrectionReason?: string,
  ) => {
    setInvoiceOperation(operation);
    setInvoiceNumber(number);
    if (
      operation === InvoiceOperationEnum.CORRECTION &&
      !!invoiceCorrectionReason
    ) {
      setOperationReason(invoiceCorrectionReason);
    } else {
      setOperationReason('');
    }

    openInvoiceOperationModal();
  };

  const handleSetOperationReason = (reason: string) => {
    setOperationReason(reason);
  };

  useEffect(() => {
    fetchResolvedInvoices(setInvoiceStatus);
  }, [fetchResolvedInvoices]);

  const handleModalConfirm = async (operation: InvoiceOperation) => {
    setIsOperationLoading(true);
    try {
      await handleConfirmInvoiceOperation(operation, setInvoiceStatus);
    } finally {
      setIsOperationLoading(false);
    }
  };

  const status = invoiceStatus?.status;
  const isEmissionInProgress =
    ResolvedInvoiceStatus.EMISSION_IN_PROGRESS === status;
  const isMissingCustomerInfo = !hasCompleteCustomerInfo;
  const isUnpacked = !isValidInvoiceStatus;
  const shouldShowTopBorder =
    invoiceStatus?.invoices && invoiceStatus?.invoices.length > 0;
  const shouldShowNotReceivedExternalInvoice =
    isExternalInvoicesEnabled &&
    (!invoiceStatus?.invoices || invoiceStatus?.invoices.length === 0);
  const invoiceSetting = useMemo(
    () => getNewAdminLink(configurationsRoutes.invoiceHistory),
    [getNewAdminLink],
  );
  const shouldShowButton = useMemo(() => {
    if (isExternalInvoicesEnabled) return false;
    if (isMissingCustomerInfo || isUnpacked) return false;

    switch (status) {
      case ResolvedInvoiceStatus.SHOW_DOCUMENTS:
      case ResolvedInvoiceStatus.EMISSION_IN_PROGRESS:
      case ResolvedInvoiceStatus.CERT_ERROR:
        return false;
      case ResolvedInvoiceStatus.ALL_CANCELLED:
      case ResolvedInvoiceStatus.ERROR_ON_EMIT:
      case ResolvedInvoiceStatus.EMPTY_DOCUMENTS:
        return true;
      default:
        return isInvoiceIssueAvailable;
    }
  }, [
    status,
    isMissingCustomerInfo,
    isUnpacked,
    isInvoiceIssueAvailable,
    isExternalInvoicesEnabled,
  ]);

  const shouldShowDocumentsError = [
    ResolvedInvoiceStatus.ERROR_ON_EMIT,
  ].includes(status as ResolvedInvoiceStatus);

  const handleButtonClick = async () => {
    setIsLoading(true);
    try {
      await handleInvoiceSubmission();
    } finally {
      setIsLoading(false);
    }
  };

  const isCertError = useMemo(
    () =>
      [ResolvedInvoiceStatus.CERT_ERROR].includes(
        status as ResolvedInvoiceStatus,
      ),
    [status],
  );

  const resolvedErrors = invoiceStatus?.errors ?? [];

  const hasAnyExternalInvoice =
    invoiceStatus?.invoices?.some((invoice) => invoice.is_external) || false;
  const hasAnyInternalInvoice =
    invoiceStatus?.invoices?.some((invoice) => !invoice.is_external) || false;

  const shouldShowExternalInvoicesUnsupported =
    !isExternalInvoicesEnabled && hasAnyExternalInvoice;
  const shouldShowUnpacked =
    !isCertError &&
    !isMissingCustomerInfo &&
    isUnpacked &&
    !isExternalInvoicesEnabled &&
    !hasAnyInternalInvoice &&
    !hasAnyExternalInvoice;

  return (
    <Card>
      <Card.Header>
        <Title as="h4">{t('cardInvoice.title')}</Title>
      </Card.Header>
      {invoiceStatus?.invoices && !shouldShowExternalInvoicesUnsupported && (
        <Invoices
          invoices={invoiceStatus.invoices}
          isExternalInvoicesEnabled={isExternalInvoicesEnabled}
          t={t}
          onOpenInvoiceOperationModal={handleOpenInvoiceOperationModal}
        />
      )}

      {shouldShowExternalInvoicesUnsupported && (
        <Box>
          <Text color="neutral-textLow">
            {t('cardInvoice.externalInvoice.unsupported')}
          </Text>
        </Box>
      )}

      {shouldShowNotReceivedExternalInvoice && (
        <Box>
          <Text fontSize="base" color="neutral-textLow">
            {t('cardInvoice.externalInvoice.notReceived')}
          </Text>
        </Box>
      )}

      {isEmissionInProgress && (
        <Box
          key="inprogress"
          paddingY="2"
          borderTopWidth={shouldShowTopBorder ? '1' : undefined}
          borderStyle="solid"
          borderColor="neutral-surfaceDisabled"
        >
          <Text fontSize="base" color="neutral-textLow">
            {t('cardInvoice.issued')}
          </Text>
        </Box>
      )}

      {shouldShowDocumentsError && (
        <Box marginTop="2">
          {resolvedErrors.length > 0 && (
            <InvoiceErrors
              resolvedErrors={resolvedErrors}
              orderDetails={orderDetails}
              t={t}
            />
          )}
        </Box>
      )}

      {status !== undefined && shouldShowButton && (
        <Box display="flex" flexDirection="row" flex="1">
          <Button
            appearance="neutral"
            onClick={handleButtonClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner color="currentColor" size="small" />
                {t('cardInvoice.button.loading')}
              </>
            ) : (
              t('cardInvoice.button.issue')
            )}
          </Button>
        </Box>
      )}

      {isCertError && (
        <Box marginTop="2">
          <Text fontSize="base" color="neutral-textLow">
            {t('cardInvoice.digitalCertificateConfig.message')}
            <Link
              as="a"
              href={invoiceSetting}
              target="_blank"
              appearance="primary"
              textDecoration="none"
            >
              {t('cardInvoice.digitalCertificateConfig.link')}
            </Link>
          </Text>
        </Box>
      )}

      {shouldShowUnpacked && (
        <Text fontSize="base" color="neutral-textLow">
          {t('cardInvoice.unpacked')}
        </Text>
      )}

      {!isCertError && isMissingCustomerInfo && (
        <Alert
          title={t('cardInvoice.alert.missingCustomerInfo.title')}
          appearance="warning"
          show
        >
          <Text fontSize="base" fontWeight="regular">
            {t('cardInvoice.alert.missingCustomerInfo.message')}
          </Text>
        </Alert>
      )}

      {isInvoiceOperationModalOpen && invoiceOperation && (
        <InvoiceOperationModal
          number={invoiceNumber}
          show={isInvoiceOperationModalOpen}
          hideModal={handleHideInvoiceOperationModal}
          isLoading={isOperationLoading}
          operation={invoiceOperation}
          operationReason={operationReason}
          setOperationReason={handleSetOperationReason}
          onConfirm={handleModalConfirm}
        />
      )}
    </Card>
  );
}

export default CardInvoice;
